import { SwarmType } from '@/graphql/types';
import type { RuntimeDataServiceInput, RuntimeDataServiceId } from './contracts';
import { buildRequiredMeasuringPointWindowInput } from './measuringPointInput';
import type { RuntimeBindingInputArgs, RuntimeTabularColumnDescriptor } from './types';
import { parseOptionalPositiveIntegerArg } from './validationPrimitives';

type SoundAlarmExceedanceServiceId = Extract<
    RuntimeDataServiceId,
    'measuring_point.leq_alarm_exceedances' | 'measuring_point.lmax_alarm_exceedances'
>;

type SoundAlarmInputBuilderArgs = RuntimeBindingInputArgs;

interface SoundAlarmInputWithLimitOptions {
    serviceLabel: string;
    argPath: string;
}

interface SoundAlarmTabularLinkConfig<TServiceId extends SoundAlarmExceedanceServiceId, TEntry> {
    label: string;
    description: string;
    columns: readonly RuntimeTabularColumnDescriptor<TEntry>[];
    parseTableRows: (value: unknown) => readonly TEntry[];
    buildInput: (args: SoundAlarmInputBuilderArgs) => RuntimeDataServiceInput<TServiceId>;
}

interface SoundAlarmLimitNoteLinkConfig<TServiceId extends SoundAlarmExceedanceServiceId> {
    label: string;
    description: string;
    buildInput: (args: SoundAlarmInputBuilderArgs) => RuntimeDataServiceInput<TServiceId>;
}

interface SoundAlarmResultLinksConfig<
    TServiceId extends SoundAlarmExceedanceServiceId,
    TEntry,
    TAlarm
> {
    entries: SoundAlarmTabularLinkConfig<TServiceId, TEntry>;
    alarms: SoundAlarmTabularLinkConfig<TServiceId, TAlarm>;
    limitReachedNote: SoundAlarmLimitNoteLinkConfig<TServiceId>;
}

export const SOUND_ALARM_EXCEEDANCE_LIMIT_ARG_OPTIONS = Array.from(
    { length: 100 / 5 },
    (_unused, index) => {
        const value = String((index + 1) * 5);
        return [value, value] as const;
    }
);

export function formatSoundAlarmLimitReachedNote(limitReachedValue: number | null): string | null {
    if (limitReachedValue === null) {
        return null;
    }

    return interpolate(
        gettext('ALARM_EXCEEDANCES_LIMIT_REACHED_NOTE'),
        { limit: limitReachedValue },
        true
    );
}

export function buildSoundAlarmInput(
    args: SoundAlarmInputBuilderArgs,
    serviceLabel: string
): ReturnType<typeof buildRequiredMeasuringPointWindowInput> {
    return buildRequiredMeasuringPointWindowInput(args, serviceLabel);
}

export function buildSoundAlarmInputWithLimit(
    args: SoundAlarmInputBuilderArgs,
    { serviceLabel, argPath }: SoundAlarmInputWithLimitOptions
) {
    const parsedLimit = parseOptionalPositiveIntegerArg(args.arg, argPath);

    return {
        ...buildSoundAlarmInput(args, serviceLabel),
        ...(typeof parsedLimit === 'undefined' ? {} : { limit: parsedLimit }),
    };
}

export function createSoundAlarmExceedanceResultLinks<
    TServiceId extends SoundAlarmExceedanceServiceId,
    TEntry,
    TAlarm
>({ entries, alarms, limitReachedNote }: SoundAlarmResultLinksConfig<TServiceId, TEntry, TAlarm>) {
    return [
        {
            key: 'entries',
            label: entries.label,
            description: entries.description,
            supportedContextKinds: ['measuring-point'],
            supportedSwarmTypes: [SwarmType.Sound],
            path: 'entries',
            valueKind: 'tabular',
            argPolicy: 'optional',
            argOptions: SOUND_ALARM_EXCEEDANCE_LIMIT_ARG_OPTIONS,
            exposeInInsertionPresets: true,
            columns: entries.columns,
            parseTableRows: entries.parseTableRows,
            buildInput: entries.buildInput,
        },
        {
            key: 'alarms',
            label: alarms.label,
            description: alarms.description,
            supportedContextKinds: ['measuring-point'],
            supportedSwarmTypes: [SwarmType.Sound],
            path: 'alarms',
            valueKind: 'tabular',
            argPolicy: 'forbidden',
            exposeInInsertionPresets: true,
            columns: alarms.columns,
            parseTableRows: alarms.parseTableRows,
            buildInput: alarms.buildInput,
        },
        {
            key: 'limit_reached_note',
            label: limitReachedNote.label,
            description: limitReachedNote.description,
            supportedContextKinds: ['measuring-point'],
            supportedSwarmTypes: [SwarmType.Sound],
            path: 'limitReachedNote',
            valueKind: 'scalar',
            argPolicy: 'optional',
            argOptions: SOUND_ALARM_EXCEEDANCE_LIMIT_ARG_OPTIONS,
            exposeInInsertionPresets: true,
            buildInput: limitReachedNote.buildInput,
        },
    ] as const;
}
